import { useState, useEffect, useCallback } from 'react';
import { callLogsApi, surveysApi } from '../services/api';
import type { CallLog, Survey, Store } from '../services/api';
import { formatDateToLocal } from '../utils/timesheetRules';
import { cacheSchedule, getCachedSchedule } from '../utils/scheduleCache';

export interface CompletedTaskEntry {
  callLogId: number;
  projectId: number;
  projectName: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  duration: number | null;
}

export interface ScheduledStore {
  id: number;
  routeId: number;
  name: string;
  number: string;
  address: string;
  city: string;
  phone: string;
  lat: number;
  lng: number;
  projects: string[];
  projectIds: number[];
  status: 'pending' | 'in_progress' | 'completed';
  checkInTime: string | null;
  checkOutTime: string | null;
  duration: number | null;
  callLogId: number | null;
  survey: Survey | null;
  /** Per-task completed entries at this store (#1954 Option A): when a
   *  rep finishes multiple tasks in one visit, each task surfaces as its
   *  own card in the Completed section of Today. Empty when no tasks are
   *  completed yet. */
  completedTasks: CompletedTaskEntry[];
}

interface UseScheduleResult {
  stores: ScheduledStore[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateStoreStatus: (storeId: number, status: ScheduledStore['status'], callLogId?: number) => void;
}

// Convert API data to our schedule format
// Note: project info is already denormalized in call log response (project_description field)
function mapCallLogToScheduledStore(
  callLog: CallLog,
  store: Store | null,
  survey: Survey | null
): ScheduledStore {
  // Use API field names (stcalltime/edcalltime)
  const checkInTime = callLog.stcalltime || null;
  const checkOutTime = callLog.edcalltime || null;
  const hasCheckedIn = !!checkInTime;
  const hasCheckedOut = !!checkOutTime;

  let status: ScheduledStore['status'] = 'pending';
  if (hasCheckedOut) {
    status = 'completed';
  } else if (hasCheckedIn) {
    status = 'in_progress';
  }

  // Calculate duration in minutes
  let duration: number | null = null;
  const callDate = callLog.dtecall;
  if (checkInTime && checkOutTime && callDate) {
    try {
      // Parse time strings (format: "HH:MM AM/PM" or "HH:MM")
      const parseTime = (dateStr: string, timeStr: string): Date | null => {
        // Try parsing as full datetime first
        const fullDate = new Date(`${dateStr}T${timeStr}`);
        if (!isNaN(fullDate.getTime())) return fullDate;
        // Otherwise just use today with the time
        const today = new Date(dateStr);
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (match) {
          let hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          const ampm = match[3];
          if (ampm) {
            if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
          }
          today.setHours(hours, minutes, 0, 0);
          return today;
        }
        return null;
      };
      const start = parseTime(callDate, checkInTime);
      const end = parseTime(callDate, checkOutTime);
      if (start && end) {
        duration = Math.round((end.getTime() - start.getTime()) / 60000);
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Use denormalized store info from call log (store lookup is optional enhancement)
  const storeId = callLog.store_legacystoreid || 0;
  // Always use legacystoreid - it's 99% populated vs 6% for storenumber
  const storeNumber = String(store?.legacystoreid || callLog.store_legacystoreid || '');
  // Construct full display name: "RetailerAbbrev #StoreNumber"
  // store.storename from API contains just the retailer abbreviation
  const retailerAbbrev = store?.storename || callLog.retailer_abbreviation || 'Store';
  const storeName = `${retailerAbbrev} #${storeNumber}`;
  const storeCity = store ? `${store.city}, ${store.state} ${store.zip}` : callLog.store_city || '';

  const projectName = callLog.project_description || callLog.description_of_activity || 'Unknown Project';
  const projectId = callLog.project_id || 0;

  // Seed completedTasks with this CallLog's row IF it's already completed.
  // The merge logic in fetchSchedule appends entries from any sibling
  // CallLogs at the same store, so a multi-task visit ends up with one
  // entry per finished task. (#1954 Option A)
  const completedTasks: CompletedTaskEntry[] = hasCheckedOut
    ? [
        {
          callLogId: callLog.calllogid,
          projectId,
          projectName,
          checkInTime,
          checkOutTime,
          duration,
        },
      ]
    : [];

  return {
    id: storeId,
    routeId: callLog.calllogid,
    name: storeName,
    number: storeNumber,
    address: store?.address || '',
    city: storeCity,
    phone: store?.phone || '',
    lat: store?.latitude || 0,
    lng: store?.longitude || 0,
    // Use project_description from call log (denormalized from backend)
    projects: [projectName],
    projectIds: [projectId],
    status,
    checkInTime,
    checkOutTime,
    duration,
    callLogId: callLog.calllogid,
    survey,
    completedTasks,
  };
}

// Build the grouped ScheduledStore[] from a raw set of call logs (the
// week the API returns). Extracted so both the live fetch and the
// offline-cache fallback (#1986) run the identical mapping pipeline.
async function buildScheduleFromCallLogs(
  allCallLogs: CallLog[],
  targetDate: string,
  opts?: { skipSurveys?: boolean }
): Promise<ScheduledStore[]> {
  if (!allCallLogs || allCallLogs.length === 0) return [];

  // Filter to only entries for the target date.
  // dtecall format from API: "MM/DD/YYYY" (e.g., "12/18/2025")
  const targetParts = targetDate.split('-'); // "2025-12-18" -> ["2025", "12", "18"]
  const targetFormatted = `${targetParts[1]}/${targetParts[2]}/${targetParts[0]}`;
  const callLogs = allCallLogs.filter((log) => log.dtecall === targetFormatted);
  if (callLogs.length === 0) return [];

  // Map call logs to scheduled stores - fetch surveys in parallel to avoid N+1 blocking.
  // Offline, each getSurveyByProject rejects and resolves to null (survey is optional).
  //
  // Cache-first paint (#2058): surveys are optional decoration and each is a
  // network call, so the instant cached render skips them entirely (surveys
  // fill in a moment later on the background revalidation). Without this the
  // "cache-first" read still stalls up to 5s on survey fetches before painting.
  let surveyResults: (Survey | null)[];
  if (opts?.skipSurveys) {
    surveyResults = callLogs.map(() => null);
  } else {
    const surveyPromises = callLogs.map(async (callLog) => {
      const projectId = callLog.project_id;
      if (projectId) {
        try {
          return await surveysApi.getSurveyByProject(projectId);
        } catch {
          return null;
        }
      }
      return null;
    });

    // Wait for all surveys in parallel (with 5 second timeout to prevent hanging)
    surveyResults = await Promise.race([
      Promise.all(surveyPromises),
      new Promise<(Survey | null)[]>((resolve) =>
        setTimeout(() => resolve(callLogs.map(() => null)), 5000)
      ),
    ]);
  }

  const scheduledStores: ScheduledStore[] = callLogs.map((callLog, index) => {
    const survey = surveyResults[index] || null;
    return mapCallLogToScheduledStore(callLog, null, survey);
  });

  // Group by store (multiple projects at same store).
  // IMPORTANT: Use compound key (retailer + legacystoreid) because legacystoreid
  // is NOT unique across retailers. Two different retailers can have store #886.
  const storeGroups = new Map<string, ScheduledStore>();
  for (const scheduled of scheduledStores) {
    const groupKey = scheduled.name; // Already includes retailer + store number
    const existing = storeGroups.get(groupKey);
    if (existing) {
      // Merge projects for same physical store, dedupe by projectId (#1947).
      for (let i = 0; i < scheduled.projectIds.length; i++) {
        const pid = scheduled.projectIds[i];
        if (!existing.projectIds.includes(pid)) {
          existing.projectIds.push(pid);
          existing.projects.push(scheduled.projects[i]);
        }
      }
      // Accumulate completedTasks across all CallLogs at this store,
      // collapsing per-project (sum durations, widest time bounds) with
      // per-callLogId dedup (#1957 follow-up — RMR diag #29).
      for (const entry of scheduled.completedTasks) {
        if (existing.completedTasks.some((c) => c.callLogId === entry.callLogId)) {
          continue;
        }
        const sibling = existing.completedTasks.find((c) => c.projectId === entry.projectId);
        if (sibling) {
          sibling.duration = (sibling.duration ?? 0) + (entry.duration ?? 0);
          if (entry.checkInTime && (!sibling.checkInTime || entry.checkInTime < sibling.checkInTime)) {
            sibling.checkInTime = entry.checkInTime;
          }
          if (entry.checkOutTime && (!sibling.checkOutTime || entry.checkOutTime > sibling.checkOutTime)) {
            sibling.checkOutTime = entry.checkOutTime;
          }
        } else {
          existing.completedTasks.push(entry);
        }
      }
      // Keep the most-active status across siblings.
      if (scheduled.status === 'in_progress' && existing.status !== 'in_progress') {
        existing.status = 'in_progress';
        existing.checkInTime = scheduled.checkInTime;
        existing.callLogId = scheduled.callLogId;
      }
    } else {
      storeGroups.set(groupKey, scheduled);
    }
  }

  return Array.from(storeGroups.values());
}

export function useSchedule(date?: string): UseScheduleResult {
  const [stores, setStores] = useState<ScheduledStore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    setError(null);

    // Get today's date if not provided (using local timezone)
    const targetDate = date || formatDateToLocal(new Date());

    // Cache-first (#2058): paint the last-cached schedule immediately so the
    // page doesn't sit on a spinner waiting for the network. We still
    // revalidate from the API below and swap in fresh data when it returns.
    // The spinner only shows while we have nothing to display.
    let hadCache = false;
    try {
      const cached = await getCachedSchedule<CallLog[]>(targetDate);
      // An empty array is a VALID cached schedule (e.g. the rep hasn't started
      // their day / has no logged calls yet) — NOT a cache miss. Counting it as
      // a hit keeps a failed offline revalidation from surfacing a false
      // "Couldn't load your schedule" banner while the page is otherwise filled
      // from cache (Nearby Calls / sets). getCachedSchedule returns null (never
      // []) when nothing was cached, so `if (cached)` distinguishes the two —
      // mirroring useCachedResource's defined-value hit check (#2057 e2e).
      if (cached) {
        // skipSurveys: keep this paint purely local (no network) so it's instant.
        setStores(await buildScheduleFromCallLogs(cached, targetDate, { skipSurveys: true }));
        hadCache = true;
        setIsLoading(false);
      }
    } catch {
      /* no usable cache — fall through to the network with the spinner up */
    }
    if (!hadCache) setIsLoading(true);

    try {
      // Fetch call logs (API returns entire work week)
      const allCallLogs = await callLogsApi.getCallLogs(targetDate);

      // Write-through to the offline cache so a later signal loss can
      // still render this date's schedule (#1986, folds in #1498).
      await cacheSchedule(targetDate, allCallLogs).catch(() => {
        /* cache write is best-effort; never block the render */
      });

      setStores(await buildScheduleFromCallLogs(allCallLogs, targetDate));
    } catch (err) {
      console.error('Failed to fetch schedule:', err);

      // If we already painted from cache, keep it and stay silent — a failed
      // background revalidation shouldn't blow away good data with an error.
      // Only surface the error when we have nothing to show (#1986, #2058).
      if (!hadCache) {
        setError(err instanceof Error ? err.message : 'Failed to load schedule');
      }
    } finally {
      setIsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const updateStoreStatus = useCallback(
    (storeId: number, status: ScheduledStore['status'], callLogId?: number) => {
      setStores((prev) =>
        prev.map((store) =>
          store.id === storeId
            ? {
                ...store,
                status,
                callLogId: callLogId || store.callLogId,
                checkInTime:
                  status === 'in_progress' || status === 'completed'
                    ? store.checkInTime || new Date().toTimeString().slice(0, 5)
                    : store.checkInTime,
                checkOutTime:
                  status === 'completed'
                    ? new Date().toTimeString().slice(0, 5)
                    : store.checkOutTime,
              }
            : store
        )
      );
    },
    []
  );

  return {
    stores,
    isLoading,
    error,
    refetch: fetchSchedule,
    updateStoreStatus,
  };
}
